import { apiClient } from '../utils/api';
import { Agency, CreateAgencyFormData } from '../types';

export interface CreateAgencyResponse {
  status: string;
  message: string;
  data: Agency;
}

export interface GetAgenciesResponse {
  status: string;
  message: string;
  data: Agency[];
}

export interface UploadLogoResponse {
  status: string;
  message: string;
  data: { logoUrl: string };
}

export interface AgencyStats {
  id: string;
  name: string;
  email: string;
  phone: string;
  isActive: boolean;
  createdAt: string;
  adminEmail: string | null;
  patientCount: number;
  caregiverCount: number;
  shiftsThisMonth: number;
  shiftsLast7Days: number;
  staffCount: number;
  lastLoginAt: string | null;
  loginsLast7Days: number;
  invoicesThisMonth: number;
}

export interface TimelineEntry {
  date: string;
  total: number;
  byAgency: Record<string, number>;
}

export interface AdminOverview {
  totalAgencies: number;
  activeAgencies: number;
  totalPatients: number;
  totalCaregivers: number;
  totalShiftsThisMonth: number;
  agencies: AgencyStats[];
  activityTimeline: TimelineEntry[];
  hiddenEmails: string[];
}

export class AgenciesService {
  static async getAdminOverview(): Promise<AdminOverview> {
    const response = await apiClient.get<{ status: string; message: string; data: AdminOverview }>('/agencies/admin/overview');
    return response.data.data;
  }

  static async getHiddenEmails(): Promise<string[]> {
    const response = await apiClient.get<{ status: string; data: string[] }>('/agencies/admin/hidden-emails');
    return response.data.data;
  }

  static async updateHiddenEmails(emails: string[]): Promise<string[]> {
    const response = await apiClient.put<{ status: string; data: string[] }>('/agencies/admin/hidden-emails', { emails });
    return response.data.data;
  }

  static async getAgencies(): Promise<Agency[]> {
    const response = await apiClient.get<GetAgenciesResponse>('/agencies');
    return response.data.data;
  }

  static async getAgency(id: string): Promise<Agency> {
    const response = await apiClient.get<CreateAgencyResponse>(`/agencies/${id}`);
    return response.data.data;
  }

  static async getCurrentAgency(): Promise<Agency> {
    const response = await apiClient.get<CreateAgencyResponse>('/agencies/current');
    return response.data.data;
  }

  static async createAgency(agencyData: CreateAgencyFormData): Promise<Agency> {
    const response = await apiClient.post<CreateAgencyResponse>('/agencies', agencyData);
    return response.data.data;
  }

  static async updateAgency(id: string, agencyData: Partial<CreateAgencyFormData>): Promise<Agency> {
    const response = await apiClient.put<CreateAgencyResponse>(`/agencies/${id}`, agencyData);
    return response.data.data;
  }

  static async updateCurrentAgency(agencyData: Partial<CreateAgencyFormData>): Promise<Agency> {
    const response = await apiClient.put<CreateAgencyResponse>('/agencies/current', agencyData);
    return response.data.data;
  }

  static async uploadCurrentAgencyLogo(
    logoFile: File, 
    onProgress?: (progress: number) => void
  ): Promise<string> {
    const formData = new FormData();
    formData.append('logo', logoFile);

    const response = await apiClient.post<UploadLogoResponse>(
      '/agencies/current/upload-logo', 
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      }
    );
    
    return response.data.data.logoUrl;
  }

  static async deactivateAgency(id: string): Promise<Agency> {
    const response = await apiClient.patch<CreateAgencyResponse>(`/agencies/${id}/deactivate`);
    return response.data.data;
  }

  static async activateAgency(id: string): Promise<Agency> {
    const response = await apiClient.patch<CreateAgencyResponse>(`/agencies/${id}/activate`);
    return response.data.data;
  }

  /**
   * GAV-Zeiterfassung (Schweiz) fuer eine Agentur ein-/ausschalten.
   * Nur Super-Admin — steuert den Sidebar-Eintrag "Zeiterfassung".
   */
  static async setGavTimeTracking(id: string, enabled: boolean): Promise<Agency> {
    const response = await apiClient.patch<CreateAgencyResponse>(
      `/agencies/${id}/gav-time-tracking`,
      { enabled },
    );
    return response.data.data;
  }

  /**
   * Kunden-API (REST + MCP) fuer eine Agentur freigeben oder sperren.
   * Nur Super-Admin — danach kann der Agentur-Admin Schluessel anlegen.
   */
  static async setApiAccess(id: string, enabled: boolean): Promise<Agency> {
    const response = await apiClient.patch<CreateAgencyResponse>(
      `/agencies/${id}/api-access`,
      { enabled },
    );
    return response.data.data;
  }

  static async deleteAgency(id: string): Promise<void> {
    await apiClient.delete(`/agencies/${id}`);
  }

  static async permanentDeleteAgency(id: string): Promise<void> {
    await apiClient.delete(`/agencies/${id}/permanent`);
  }

  static async getInvoicePrefixes(): Promise<Array<{ prefix: string; counter: number; label: string; nextNumber: number }>> {
    const response = await apiClient.get<{
      status: string;
      message: string;
      data: Array<{ prefix: string; counter: number; label: string; nextNumber: number }>
    }>('/agencies/current/invoice-prefixes');
    return response.data.data;
  }

  static async updateInvoicePrefixes(
    prefixes: Array<{ prefix: string; counter: number; label: string }>
  ): Promise<Agency> {
    const response = await apiClient.put<CreateAgencyResponse>(
      '/agencies/current/invoice-prefixes',
      { prefixes }
    );
    return response.data.data;
  }

  static async sendAgencyEmail(
    agencyId: string,
    subject: string,
    html: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      '/agencies/admin/send-email',
      { agencyId, subject, html },
    );
    return response.data;
  }

  static async testSmtpConnection(): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      '/agencies/current/test-smtp'
    );
    return response.data;
  }
} 